// src/features/dashboard/utils/chartTransformers.js
import { generateColors } from "../../../utility/utils";

// ── Helpers ───────────────────────────────────────────────────
const capitalizeFirst = (str) => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const extractName = (email) => {
  if (!email) return "";
  const namePart = email.split("@")[0];
  const parts = namePart.split(/[._]/);
  return parts.map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join(" ");
};

// ── User Interaction Chart ────────────────────────────────────
export const transformUserInteractionData = (userInteraction = []) => {
  const sorted = Array.isArray(userInteraction)
    ? [...userInteraction].sort((a, b) =>
        a.user_email.localeCompare(b.user_email),
      )
    : [];

  return {
    // labels: sorted.map((item) => item.user_email?.split("@")[0]),
    labels: sorted.map((item) => extractName(item.user_email)),
    sessionCounts: sorted.map((item) => item.session_count),
    avgDurations: sorted.map((item) => item.avg_session_duration_min),
  };
};

export const buildSessionChartData = (labels, sessionCounts, avgDurations) => ({
  labels,
  datasets: [
    {
      label: "Interaction Count",
      data: sessionCounts,
      borderWidth: 1,
      barThickness: 20,
      gradientFrom: "#378894ff",
      gradientTo: "#1c383c",
    },
    {
      label: "Avg Interaction Duration (min)",
      data: avgDurations,
      gradientFrom: "#67a9eaff",
      gradientTo: "#0063C3",
      borderWidth: 1,
      barThickness: 20,
    },
  ],
});

// ── Weekly Users Chart ────────────────────────────────────────
export const transformWeeklyUsersData = (weeklyUsers) => {
  const data = weeklyUsers?.data || [];
  return {
    labels: data.map((item) => item.time_metric ?? item.week_label),
    datasets: [
      {
        label: "Max Users",
        data: data.map((item) => item.max_users),
        max_date: data.map((item) => item.max_date),
        gradientFrom: "#67a9eaff",
        gradientTo: "#0063C3",
      },
      {
        label: "Min Users",
        data: data.map((item) => item.min_users),
        min_date: data.map((item) => item.min_date),
        gradientFrom: "#378894ff",
        gradientTo: "#1c383c",
      },
    ],
  };
};

// ── BU Count Chart ────────────────────────────────────────────
export const transformBuCountData = (buCount) => {
  const units = buCount?.data || [];
  return {
    labels: units.map((item) => item.unit.toUpperCase()),
    datasets: [
      {
        label: "Total Users",
        data: units.map((item) => Number(item.total_users) || 0),
        gradientFrom: "#67a9eaff",
        gradientTo: "#0063C3",
      },
      {
        label: "Active Users",
        data: units.map((item) => Number(item.active_users) || 0),
        gradientFrom: "#378894ff",
        gradientTo: "#1c383c",
      },
      {
        label: "Returning Users",
        data: units.map((item) => Number(item.returning_users) || 0),
        gradientFrom: "#C9A7FF",
        gradientTo: "#5A00B8",
      },
    ],
  };
};

// ── Question Type Chart ───────────────────────────────────────
export const transformQuestionDistData = (questionDist = []) => {
  const safe = Array.isArray(questionDist) ? questionDist : [];
  return {
    labels: safe.map((item) => capitalizeFirst(item.question_type || "N/A")),
    datasets: [
      {
        label: "Question Type Distribution",
        data: safe.map((item) => item.percentage || 0),
        backgroundColor: generateColors(safe.length),
        count: safe.map((item) => item.question_count || 0),
      },
    ],
  };
};

// ── Feedback Type Stacked Bar ─────────────────────────────────
export const transformFeedbackDistData = (feedbackDist) => {
  if (!feedbackDist?.data || typeof feedbackDist.data !== "object") {
    return { categories: [], series: [] };
  }

  const dataset = feedbackDist.data;
  let categories = Object.keys(dataset);

  const feedbackTypes = [
    ...new Set(
      Object.values(dataset)
        .flat()
        .map((item) => item.category),
    ),
  ];

  let series = feedbackTypes
    .map((type) => {
      if (!type) return null;
      const formattedName =
        type.charAt(0).toUpperCase() + type.slice(1).replace("_", " ");
      const dataPoints = categories.map((key) => {
        const entry = dataset[key]?.find((item) => item.category === type);
        return entry ? entry.count || 0 : 0;
      });
      return { name: formattedName, data: dataPoints };
    })
    .filter(Boolean);

  const activeCategoryIndexes = categories
    .map((_, index) => index)
    .filter((i) => series.some((s) => s.data[i] > 0));

  categories = categories.filter((_, i) => activeCategoryIndexes.includes(i));
  series = series.map((s) => ({
    ...s,
    data: s.data.filter((_, i) => activeCategoryIndexes.includes(i)),
  }));

  return { categories, series };
};
